import type { AppSettingsDto } from 'api/generated';
import { themePresets } from 'lib/app-configs';
import type { ApiClientHeaders } from './tanstack-query-client';
export type AppSettingsResult = {
  appSettings: AppSettingsDto;
  headers: ApiClientHeaders;
};

// Off in development so backend changes show up on the next reload.
const cacheTtlMs = process.env.NODE_ENV === 'development' ? 0 : 60_000;
const cacheMaxEntries = 100;
const cache = new Map<string, {
  promise: Promise<AppSettingsResult>;
  expiresAt: number;
}>();

/** Exposed for tests. */
export function clearAppSettingsCache() {
  cache.clear();
}

/**
 * Server-side fetch for backend /api/appsettings, resolving tenant/skin/region
 * for the supplied Origin. Shared by:
 *  - _app.getInitialProps (called directly server-side — no HTTP loopback,
 *    which avoids the trailingSlash + 404 cascade that triggered Vercel's
 *    loop detection)
 *  - pages/api/appsettings-proxy (kept for parity with secure-wallet and any
 *    future client-side use)
 *
 * NEXT_PUBLIC_SKIP_PROXY: overrides tenant/skin/protocol/region from env vars
 * for local dev against a backend that doesn't have skin mappings for the
 * incoming Origin. Body is still real data from the backend.
 *
 * Results are cached in memory per origin for `cacheTtlMs`, and concurrent
 * requests for the same origin share one backend call. Failures are not
 * cached, so the next request retries.
 */
export function fetchAppSettings(origin: string): Promise<AppSettingsResult> {
  if (cacheTtlMs <= 0) {
    return requestAppSettings(origin);
  }
  const now = Date.now();
  const cached = cache.get(origin);
  if (cached && cached.expiresAt > now) {
    return cached.promise;
  }
  const promise = requestAppSettings(origin);
  promise.catch(() => {
    if (cache.get(origin)?.promise === promise) {
      cache.delete(origin);
    }
  });

  // The key comes from the request's Host header, so bound the map in case
  // arbitrary hosts reach the server. Map iteration order is insertion order.
  cache.delete(origin);
  if (cache.size >= cacheMaxEntries) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(origin, {
    promise,
    expiresAt: now + cacheTtlMs
  });
  return promise;
}
async function requestAppSettings(origin: string): Promise<AppSettingsResult> {
  // E2E: the appsettings fetch happens server-side during SSR and so cannot be
  // intercepted by Playwright's browser-level route mocks. Return a static stub
  // instead of hitting a live backend, keeping the test run hermetic. Gated by
  // an env var that is only set by the e2e webserver (see playwright.config.ts).
  if (process.env.E2E_MOCK_APPSETTINGS === 'true') {
    return e2eAppSettings();
  }
  const baseUrl = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8083';
  const fetchHeaders: Record<string, string> = {};
  if (origin) {
    fetchHeaders['Origin'] = origin;
  }
  const tenantId = process.env.TENANT_ID || process.env.NEXT_PUBLIC_TENANT_ID;
  const skinUrl = process.env.SKIN_URL || process.env.NEXT_PUBLIC_SKIN_URL;
  if (tenantId) {
    fetchHeaders['TenantId'] = tenantId;
  }
  if (skinUrl) {
    fetchHeaders['SkinUrl'] = skinUrl;
  }

  // redirect: 'manual' — if a misconfigured backend redirects unknown origins
  // back to the frontend, following it would re-enter this codebase and loop.
  // Surface redirects as errors instead.
  const res = await fetch(baseUrl + '/api/appsettings', {
    headers: fetchHeaders,
    redirect: 'manual'
  });
  if (res.status >= 300 && res.status < 400) {
    throw new Error(`appsettings: unexpected redirect from backend (status ${res.status})`);
  }
  if (!res.ok) {
    throw new Error(`appsettings: backend returned ${res.status}`);
  }
  const appSettings = (await res.json()) as AppSettingsDto;
  if (process.env.NEXT_PUBLIC_SKIP_PROXY === 'true' || process.env.SKIP_PROXY === 'true') {
    const apiUrl = new URL(baseUrl);
    return {
      appSettings,
      headers: {
        tenantId: tenantId ?? null,
        skinUrl: skinUrl ?? null,
        protocol: apiUrl.protocol.replace(':', ''),
        regionalHost: apiUrl.host
      }
    };
  }
  return {
    appSettings,
    headers: {
      tenantId: res.headers.get('tenant-id'),
      skinUrl: res.headers.get('skin-url'),
      protocol: res.headers.get('protocol'),
      regionalHost: res.headers.get('regional-host')
    }
  };
}

/** Static appsettings used by e2e runs so SSR doesn't depend on a live backend. */
function e2eAppSettings(): AppSettingsResult {
  const locale: AppSettingsDto['defaultLocale'] = {
    name: 'English',
    culture: 'en-GB',
    twoLetterISOCulture: 'en',
    position: 0
  };
  const currency: AppSettingsDto['defaultCurrency'] = {
    currencyKey: 'USD',
    position: 0,
    value: 1
  };
  return {
    appSettings: {
      name: 'E2E',
      phoneNumber: null,
      openingHours: null,
      availableCurrencies: [currency, {
        currencyKey: 'GBP',
        position: 1,
        value: 1
      }],
      defaultCurrency: currency,
      availableLocales: [locale],
      defaultLocale: locale,
      externalAuthProviders: [],
      // A tenant that ships a dark theme, so the site follows the browser.
      defaultColorScheme: null,
      theme: themePresets.default.theme as AppSettingsDto['theme'],
      darkTheme: themePresets.default.darkTheme as AppSettingsDto['darkTheme'],
      timezone: 'Atlantic/Reykjavik'
    },
    headers: {
      tenantId: 'e2e',
      skinUrl: 'localhost',
      protocol: 'http',
      regionalHost: 'localhost'
    }
  };
}